/**
 * 会话状态（**不落任何本地存储**）。
 *
 * 为什么不用 localStorage 当登录依据：
 *   cookie 是 HttpOnly 的，前端读不到、也删不掉；本地标记只能说明「这台浏览器
 *   曾经登录过」，不能说明「服务端现在认这个会话」。令牌/会话过期后本地标记还在，
 *   就会把用户带进一个所有请求都 401 的空壳页面。
 *   所以本 store 只是 `/v1/auth/me` 结果的**内存缓存**，唯一的真相是服务端。
 *
 * `status` 的状态机：
 *   unknown → checking → authenticated
 *                      ↘ unauthenticated（401）
 *                      ↘ error（网络/5xx：**不能**当成未登录，否则一次抖动就把人踢出去）
 *
 * 本文件是叶子模块（只依赖 zustand），避免与 apiClient 形成循环依赖。
 */

import { create } from "zustand";
import type { AuthUser } from "../lib/apiTypes.js";

export type SessionStatus = "unknown" | "checking" | "authenticated" | "unauthenticated" | "error";

interface AuthState {
  readonly status: SessionStatus;
  readonly user: AuthUser | null;
  readonly error: string | null;
  setChecking: () => void;
  setAuthenticated: (user: AuthUser) => void;
  markUnauthenticated: () => void;
  setError: (message: string) => void;
  reset: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  status: "unknown",
  user: null,
  error: null,
  setChecking: () => set({ status: "checking", error: null }),
  setAuthenticated: (user) => set({ status: "authenticated", user, error: null }),
  markUnauthenticated: () => set({ status: "unauthenticated", user: null, error: null }),
  setError: (error) => set({ status: "error", user: null, error }),
  reset: () => set({ status: "unknown", user: null, error: null }),
}));
